import type { Locale } from "../i18n/siteCopy";

export type NewsArticle = {
  slug: string;
  tag: string;
  title: string;
  summary: string;
  date: string;
  image: string;
  imageFit?: "cover" | "contain";
  detailEyebrow: string;
  detailTitle: string;
  intro: string;
  sections: Array<{
    title: string;
    body: string;
  }>;
  highlights: string[];
  galleryImages?: Array<{
    src: string;
    alt: string;
    fit?: "cover" | "contain";
  }>;
};

const newsByLocale: Record<Locale, NewsArticle[]> = {
  en: [
    {
      slug: "melomeli-bpom-indonesia-certification",
      tag: "Regulatory Update",
      title: "QIANDU Indonesia secured BPOM qualification, with MELOMELI completing BPOM registration for the Indonesian market.",
      summary:
        "The update confirms QIANDU's regulatory progress in Indonesia and marks MELOMELI's successful BPOM filing and product compliance entry.",
      date: "June 2026",
      image: "/company-news/melomeli-huonggiang-02.jpg",
      imageFit: "contain",
      detailEyebrow: "Regulatory Update",
      detailTitle: "QIANDU Indonesia obtained BPOM qualification, and MELOMELI completed BPOM registration in Indonesia.",
      intro:
        "QIANDU Indonesia has completed BPOM-related qualification progress, and MELOMELI has now successfully passed BPOM registration and filing procedures for Indonesia. This marks a concrete regulatory milestone for localized market entry and compliant channel expansion.",
      sections: [
        {
          title: "Regulatory Progress",
          body:
            "The BPOM qualification milestone strengthens QIANDU's operational readiness in Indonesia and provides a clearer compliance foundation for localized beauty brand development in the market.",
        },
        {
          title: "Brand Certification",
          body:
            "MELOMELI's successful BPOM registration confirms that the brand has completed product filing and certification procedures required for compliant entry into the Indonesian market.",
        },
        {
          title: "Market Meaning",
          body:
            "For QIANDU, this is not only a regulatory milestone but also a practical commercial step that supports local distribution, retail expansion, and brand-building activity in Indonesia.",
        },
      ],
      highlights: ["Indonesia", "BPOM", "MELOMELI"],
    },
    {
      slug: "peak-jmsolution-bangkok-activation",
      tag: "Corporate News",
      title: "QIANDU GROUP, Wishco, and Thai celebrity PEAK completed a JMsolution offline retail activation in Bangkok.",
      summary:
        "The activation supported local retail visibility and channel entry for JMsolution in the Bangkok beauty market.",
      date: "May 30, 2026",
      image: "/company-news/peak-jmsolution-03.jpg",
      imageFit: "contain",
      detailEyebrow: "Corporate News",
      detailTitle: "QIANDU GROUP completed a Bangkok offline retail activation for JMsolution with Wishco and PEAK.",
      intro:
        "On May 30, 2026, QIANDU GROUP joined Wishco and Thai celebrity PEAK in Bangkok for an offline retail activation centered on JMsolution. The event was designed to strengthen in-store visibility, improve local consumer awareness, and support channel-facing promotion for the brand.",
      sections: [
        {
          title: "Event Overview",
          body:
            "The activation brought together brand presentation, celebrity participation, and offline retail exposure in a single market-facing event. JMsolution product visuals, campaign materials, and on-site engagement were structured to create a clearer local retail impression.",
        },
        {
          title: "Market Significance",
          body:
            "For QIANDU, the Bangkok activation represented a practical step in supporting Korean beauty brand entry into local beauty chains. The program reinforced brand credibility in-market while giving channel partners a more visible consumer-facing moment.",
        },
        {
          title: "Execution Value",
          body:
            "The campaign demonstrated QIANDU's ability to combine brand partnership, regional execution, and offline promotion into a more structured market-entry format for Korean beauty brands across Southeast Asia.",
        },
      ],
      highlights: ["Bangkok", "JMsolution", "Offline retail activation"],
      galleryImages: [
        { src: "/company-news/peak-jmsolution-04.jpg", alt: "PEAK and QIANDU GROUP representative at the Bangkok activation", fit: "contain" },
        { src: "/company-news/peak-jmsolution-03.jpg", alt: "PEAK with JMsolution campaign visual", fit: "contain" },
        { src: "/company-news/peak-jmsolution-02.jpg", alt: "QIANDU and Wishco presentation on stage", fit: "contain" },
        { src: "/company-news/peak-jmsolution-01.jpg", alt: "PEAK with QIANDU JMsolution signage", fit: "contain" },
      ],
    },
    {
      slug: "huong-giang-melomeli-partnership",
      tag: "Partnership Update",
      title: "Vietnamese entertainer Huong Giang was appointed promotional partner for the Korean MELOMELI cleansing water campaign.",
      summary:
        "The collaboration expands MELOMELI brand visibility in Vietnam through a localized celebrity-led communication campaign.",
      date: "May 2026",
      image: "/company-news/melomeli-huonggiang-01.jpg",
      imageFit: "contain",
      detailEyebrow: "Partnership Update",
      detailTitle: "Huong Giang joined the Korean MELOMELI cleansing water campaign as a promotional partner in Vietnam.",
      intro:
        "QIANDU confirmed a new promotional partnership connecting Vietnamese entertainer Huong Giang with the Korean MELOMELI cleansing water campaign. The collaboration is intended to strengthen localized market communication and broaden brand recognition in Vietnam.",
      sections: [
        {
          title: "Partnership Scope",
          body:
            "The cooperation centers on campaign exposure, product communication, and consumer-facing brand storytelling for the MELOMELI cleansing water line. Huong Giang's profile supports a clearer and more localized route to audience engagement.",
        },
        {
          title: "Brand Relevance",
          body:
            "MELOMELI is being positioned through a cleaner and more focused skincare narrative, with cleansing water as a lead product in the communication strategy. This partnership helps translate that positioning into local media and social visibility.",
        },
        {
          title: "Regional Value",
          body:
            "For QIANDU, the program reflects a broader operating model that links Korean beauty brands with localized partner execution, influencer-facing communication, and market-specific rollout support across Southeast Asia.",
        },
      ],
      highlights: ["Vietnam", "MELOMELI", "Localized celebrity campaign"],
      galleryImages: [
        { src: "/company-news/melomeli-huonggiang-01.jpg", alt: "Huong Giang for MELOMELI partner campaign", fit: "contain" },
        { src: "/company-news/melomeli-huonggiang-02.jpg", alt: "MELOMELI cleansing water product exposure", fit: "contain" },
      ],
    },
    {
      slug: "qiandu-portfolio-update-2026",
      tag: "Company News",
      title: "QIANDU continues refining its global K-Beauty portfolio.",
      summary:
        "The portfolio continues to evolve around distributor-ready Korean beauty brands and hero product selections.",
      date: "June 2026",
      image: "/official-brands/roundlab.jpg",
      detailEyebrow: "Company Update",
      detailTitle: "QIANDU is continuing to refine its K-Beauty portfolio for global partners.",
      intro:
        "QIANDU is continuing to sharpen its portfolio structure around clearer brand positioning, stronger hero products, and more distribution-ready market presentation for global partners.",
      sections: [
        {
          title: "Portfolio Direction",
          body:
            "The current portfolio is being refined to emphasize recognizable Korean beauty brands, focused category stories, and stronger product-led presentation.",
        },
        {
          title: "Partner Readiness",
          body:
            "Brand selection is being aligned more closely with distributor needs, local retail fit, and market-entry conversations across APAC markets.",
        },
      ],
      highlights: ["Portfolio curation", "Hero product focus", "Distributor readiness"],
    },
  ],
  zh: [
    {
      slug: "melomeli-bpom-indonesia-certification",
      tag: "资质认证",
      title: "印尼千渡公司已获得 BPOM 资质认证，其中 MELOMELI 已通过 BPOM 认证并完成印尼备案。",
      summary: "该进展标志着千渡在印尼市场的合规能力进一步落地，也意味着 MELOMELI 已完成进入印尼市场的重要监管流程。",
      date: "2026年6月",
      image: "/company-news/melomeli-huonggiang-02.jpg",
      imageFit: "contain",
      detailEyebrow: "资质认证",
      detailTitle: "印尼千渡公司获得 BPOM 资质认证，MELOMELI 已通过 BPOM 认证并完成印尼备案。",
      intro:
        "印尼千渡公司已完成 BPOM 相关资质认证推进，其中 MELOMELI 已正式通过 BPOM 认证并完成印尼备案流程。这意味着品牌在印尼市场的合规进入和本地渠道拓展具备了更明确的监管基础。",
      sections: [
        {
          title: "资质进展",
          body:
            "BPOM 资质认证的取得，进一步强化了千渡在印尼市场的本地化合规运营能力，也为后续更多韩国美妆品牌在当地市场落地提供了更清晰的执行基础。",
        },
        {
          title: "品牌备案",
          body:
            "MELOMELI 已完成 BPOM 认证和印尼备案流程，意味着该品牌已经具备了进入印尼市场所需的重要产品合规条件。",
        },
        {
          title: "市场意义",
          body:
            "对千渡而言，这不仅是监管层面的进展，也是在印尼市场推进品牌分销、零售合作与本地化品牌建设的重要一步。",
        },
      ],
      highlights: ["印尼", "BPOM", "MELOMELI"],
    },
    {
      slug: "peak-jmsolution-bangkok-activation",
      tag: "公司新闻",
      title: "2026年5月30日，QIANDU GROUP 联合 Wishco 与泰国明星 PEAK 在曼谷完成 JMsolution 线下零售推广活动。",
      summary: "该活动围绕线下零售推广与门店曝光展开，进一步提升了 JMsolution 在曼谷市场的渠道可见度。",
      date: "2026年5月30日",
      image: "/company-news/peak-jmsolution-03.jpg",
      imageFit: "contain",
      detailEyebrow: "公司新闻",
      detailTitle: "QIANDU GROUP 联合 Wishco 与泰国明星 PEAK 在曼谷完成 JMsolution 线下零售推广活动。",
      intro:
        "2026年5月30日，QIANDU GROUP 携手 Wishco 与泰国明星 PEAK 在曼谷开展 JMsolution 线下零售推广活动，围绕品牌曝光、渠道展示与终端传播展开本地化执行。",
      sections: [
        {
          title: "活动概况",
          body:
            "本次活动将品牌展示、明星合作与线下零售推广结合在同一市场场景中，重点突出 JMsolution 在当地终端渠道中的品牌形象与产品认知。",
        },
        {
          title: "市场意义",
          body:
            "对千渡而言，该活动不仅服务于单次曝光，更是支持韩国美妆品牌进入泰国线下美妆连锁渠道的一次市场执行动作，增强了品牌在本地市场的零售可见度。",
        },
        {
          title: "执行价值",
          body:
            "活动体现了千渡将品牌合作、区域渠道落地与本地化推广整合为统一执行体系的能力，为韩国美妆品牌在东南亚市场的渠道拓展提供更清晰的落地路径。",
        },
      ],
      highlights: ["曼谷", "JMsolution", "线下零售推广"],
      galleryImages: [
        { src: "/company-news/peak-jmsolution-04.jpg", alt: "PEAK 与 QIANDU GROUP 代表活动合影", fit: "contain" },
        { src: "/company-news/peak-jmsolution-03.jpg", alt: "PEAK 持 JMsolution 活动牌合影", fit: "contain" },
        { src: "/company-news/peak-jmsolution-02.jpg", alt: "QIANDU 与 Wishco 活动现场", fit: "contain" },
        { src: "/company-news/peak-jmsolution-01.jpg", alt: "PEAK 与 QIANDU JMsolution 标识合影", fit: "contain" },
      ],
    },
    {
      slug: "huong-giang-melomeli-partnership",
      tag: "合作动态",
      title: "越南艺人 Huong Giang 正式成为韩国 MELOMELI 卸妆水推广宣传合作伙伴。",
      summary: "本次合作将围绕本地化内容传播与明星合作推广，强化 MELOMELI 在越南市场的品牌认知。",
      date: "2026年5月",
      image: "/company-news/melomeli-huonggiang-01.jpg",
      imageFit: "contain",
      detailEyebrow: "合作动态",
      detailTitle: "Huong Giang 正式加入韩国 MELOMELI 卸妆水越南市场推广合作。",
      intro:
        "QIANDU 正式确认越南艺人 Huong Giang 成为韩国 MELOMELI 卸妆水推广宣传合作伙伴。该合作将服务于品牌在越南市场的本地化传播、产品沟通与消费者触达。",
      sections: [
        {
          title: "合作内容",
          body:
            "本次合作以 MELOMELI 卸妆水为核心产品，通过明星合作传播、社交内容露出与本地市场沟通，强化品牌在越南市场的产品认知与话题度。",
        },
        {
          title: "品牌价值",
          body:
            "MELOMELI 当前以更聚焦的清洁护理产品语言面向市场，本次合作有助于将品牌形象从产品卖点进一步延伸到本地消费沟通与内容传播层面。",
        },
        {
          title: "区域意义",
          body:
            "对千渡而言，该项目体现了以韩国品牌为基础、结合本地合作伙伴与区域传播执行的运营能力，为韩国美妆品牌在东南亚市场建立更有效的市场进入路径。",
        },
      ],
      highlights: ["越南", "MELOMELI", "本地化明星合作"],
      galleryImages: [
        { src: "/company-news/melomeli-huonggiang-01.jpg", alt: "Huong Giang 与 MELOMELI 合作海报", fit: "contain" },
        { src: "/company-news/melomeli-huonggiang-02.jpg", alt: "MELOMELI 卸妆水产品露出", fit: "contain" },
      ],
    },
    {
      slug: "qiandu-portfolio-update-2026",
      tag: "公司",
      title: "韩国美妆品牌组合持续升级",
      summary: "QIANDU 持续完善重点品牌与核心产品组合，以支持国际市场的渠道合作与品牌展示。",
      date: "2026年6月",
      image: "/official-brands/roundlab.jpg",
      detailEyebrow: "公司更新",
      detailTitle: "QIANDU 持续完善韩国美妆品牌组合与国际化展示结构。",
      intro:
        "QIANDU 正围绕更清晰的品牌定位、更聚焦的核心产品以及更适合渠道合作的展示方式，持续优化韩国美妆品牌组合。",
      sections: [
        {
          title: "组合方向",
          body:
            "当前组合优化重点在于提升重点品牌识别度、强化核心产品线展示，并让整体品牌结构更适合国际合作伙伴阅读与理解。",
        },
        {
          title: "合作导向",
          body:
            "品牌选择与市场表达正在更紧密地围绕渠道适配、零售场景与区域市场进入讨论展开，以提升合作沟通效率。",
        },
      ],
      highlights: ["品牌组合", "核心产品", "渠道合作"],
    },
  ],
  ko: [
    {
      slug: "melomeli-bpom-indonesia-certification",
      tag: "인증 업데이트",
      title: "인도네시아 QIANDU 법인이 BPOM 자격을 확보했으며, MELOMELI는 BPOM 인증 및 인도네시아 등록을 완료했습니다.",
      summary:
        "이번 진전은 QIANDU의 인도네시아 현지 규제 대응 역량을 보여주며, MELOMELI의 시장 진입 준비가 공식적으로 한 단계 진전되었음을 의미합니다.",
      date: "2026년 6월",
      image: "/company-news/melomeli-huonggiang-02.jpg",
      imageFit: "contain",
      detailEyebrow: "인증 업데이트",
      detailTitle: "인도네시아 QIANDU 법인이 BPOM 자격을 확보했고, MELOMELI는 BPOM 인증과 현지 등록을 마쳤습니다.",
      intro:
        "인도네시아 QIANDU 법인은 BPOM 관련 자격 확보를 완료했으며, MELOMELI는 인도네시아 시장 진입에 필요한 BPOM 인증 및 등록 절차를 성공적으로 마무리했습니다. 이는 현지 규제 준수와 시장 확장을 위한 중요한 기반입니다.",
      sections: [
        {
          title: "규제 진전",
          body:
            "BPOM 자격 확보는 QIANDU의 인도네시아 현지 운영 준비도를 높이는 동시에, 향후 한국 뷰티 브랜드의 현지 진출을 위한 규제 기반을 더 명확히 합니다.",
        },
        {
          title: "브랜드 등록",
          body:
            "MELOMELI의 BPOM 인증 및 등록 완료는 해당 브랜드가 인도네시아 시장 진입에 필요한 핵심 제품 규제 절차를 마쳤다는 뜻입니다.",
        },
        {
          title: "시장 의미",
          body:
            "이는 단순한 인증 소식이 아니라, 인도네시아에서의 유통, 리테일 협업, 현지 브랜드 구축을 추진하기 위한 실질적인 사업 기반으로 볼 수 있습니다.",
        },
      ],
      highlights: ["인도네시아", "BPOM", "MELOMELI"],
    },
    {
      slug: "peak-jmsolution-bangkok-activation",
      tag: "기업 소식",
      title: "2026년 5월 30일, QIANDU GROUP은 Wishco 및 태국 셀럽 PEAK과 함께 방콕에서 JMsolution 오프라인 리테일 프로모션을 진행했습니다.",
      summary:
        "이번 프로모션은 방콕 뷰티 리테일 채널에서 JMsolution의 오프라인 가시성과 유통 접점을 강화하기 위한 활동입니다.",
      date: "2026년 5월 30일",
      image: "/company-news/peak-jmsolution-03.jpg",
      imageFit: "contain",
      detailEyebrow: "기업 소식",
      detailTitle: "QIANDU GROUP은 Wishco 및 PEAK과 함께 방콕에서 JMsolution 오프라인 리테일 프로모션을 진행했습니다.",
      intro:
        "2026년 5월 30일, QIANDU GROUP은 Wishco 및 태국 셀럽 PEAK과 함께 방콕에서 JMsolution 오프라인 리테일 프로모션을 진행했습니다. 이번 활동은 브랜드 노출, 채널 홍보, 현지 시장 커뮤니케이션을 중심으로 기획되었습니다.",
      sections: [
        {
          title: "행사 개요",
          body:
            "행사는 브랜드 비주얼, 셀럽 참여, 오프라인 프로모션 요소를 결합해 JMsolution의 현지 리테일 인지도를 보다 명확하게 전달하는 방식으로 진행되었습니다.",
        },
        {
          title: "시장 의미",
          body:
            "이번 프로젝트는 단순 홍보를 넘어 한국 뷰티 브랜드의 태국 오프라인 뷰티 체인 진입을 지원하는 시장 실행 사례로서 의미가 있습니다.",
        },
        {
          title: "실행 가치",
          body:
            "QIANDU는 브랜드 협업, 지역 채널 실행, 현지 프로모션을 하나의 운영 흐름으로 연결해 동남아 시장에서의 진입 구조를 보다 구체화하고 있습니다.",
        },
      ],
      highlights: ["방콕", "JMsolution", "오프라인 리테일 프로모션"],
      galleryImages: [
        { src: "/company-news/peak-jmsolution-04.jpg", alt: "PEAK and QIANDU GROUP representative at the event", fit: "contain" },
        { src: "/company-news/peak-jmsolution-03.jpg", alt: "PEAK with JMsolution campaign board", fit: "contain" },
        { src: "/company-news/peak-jmsolution-02.jpg", alt: "QIANDU and Wishco event stage", fit: "contain" },
        { src: "/company-news/peak-jmsolution-01.jpg", alt: "PEAK with QIANDU JMsolution signage", fit: "contain" },
      ],
    },
    {
      slug: "huong-giang-melomeli-partnership",
      tag: "협업 소식",
      title: "베트남 엔터테이너 Huong Giang이 한국 MELOMELI 클렌징 워터 캠페인의 홍보 파트너로 공식 합류했습니다.",
      summary:
        "이번 협업은 현지화된 셀럽 마케팅을 통해 베트남 시장에서 MELOMELI의 브랜드 인지도를 확대하기 위한 것입니다.",
      date: "2026년 5월",
      image: "/company-news/melomeli-huonggiang-01.jpg",
      imageFit: "contain",
      detailEyebrow: "협업 소식",
      detailTitle: "Huong Giang이 한국 MELOMELI 클렌징 워터의 베트남 캠페인 홍보 파트너로 합류했습니다.",
      intro:
        "QIANDU는 베트남 엔터테이너 Huong Giang이 한국 MELOMELI 클렌징 워터 홍보 캠페인의 공식 파트너로 참여한다고 확인했습니다. 이번 협업은 현지화된 브랜드 커뮤니케이션과 시장 인지도 확대를 목표로 합니다.",
      sections: [
        {
          title: "협업 범위",
          body:
            "이번 프로젝트는 클렌징 워터 제품을 중심으로 캠페인 노출, 콘텐츠 커뮤니케이션, 셀럽 기반 브랜드 스토리텔링을 강화하는 데 초점이 맞춰져 있습니다.",
        },
        {
          title: "브랜드 의미",
          body:
            "MELOMELI는 보다 정제된 클렌징 케어 브랜드 무드로 시장에 제시되고 있으며, 이번 협업은 그 포지셔닝을 베트남 소비자 커뮤니케이션으로 확장하는 역할을 합니다.",
        },
        {
          title: "지역 실행 가치",
          body:
            "QIANDU 입장에서는 한국 브랜드, 현지 파트너십, 시장별 홍보 실행을 연결하는 운영 구조를 보여주는 사례로서 의미가 있습니다.",
        },
      ],
      highlights: ["베트남", "MELOMELI", "현지화 셀럽 캠페인"],
      galleryImages: [
        { src: "/company-news/melomeli-huonggiang-01.jpg", alt: "Huong Giang for MELOMELI campaign", fit: "contain" },
        { src: "/company-news/melomeli-huonggiang-02.jpg", alt: "MELOMELI cleansing water product exposure", fit: "contain" },
      ],
    },
    {
      slug: "qiandu-portfolio-update-2026",
      tag: "회사",
      title: "K-뷰티 포트폴리오를 계속 확장하고 있습니다.",
      summary:
        "QIANDU는 핵심 브랜드와 대표 제품 구성을 지속적으로 정비하며 글로벌 파트너 협업 기반을 강화하고 있습니다.",
      date: "2026년 6월",
      image: "/official-brands/roundlab.jpg",
      detailEyebrow: "회사 업데이트",
      detailTitle: "QIANDU는 글로벌 파트너를 위한 K-뷰티 포트폴리오 구성을 지속적으로 고도화하고 있습니다.",
      intro:
        "QIANDU는 보다 명확한 브랜드 포지셔닝, 핵심 제품 중심의 구성, 채널 친화적인 표현 방식을 기준으로 K-뷰티 포트폴리오를 지속적으로 정비하고 있습니다.",
      sections: [
        {
          title: "포트폴리오 방향",
          body:
            "현재 포트폴리오 조정은 인지도 높은 한국 뷰티 브랜드와 명확한 히어로 제품 구성을 중심으로 진행되고 있습니다.",
        },
        {
          title: "협업 준비도",
          body:
            "브랜드 선택과 표현 구조는 APAC 시장의 유통 파트너, 리테일 적합성, 시장 진입 대화에 보다 적합하도록 정돈되고 있습니다.",
        },
      ],
      highlights: ["포트폴리오 큐레이션", "핵심 제품", "유통 파트너 적합성"],
    },
  ],
  vi: [
    {
      slug: "melomeli-bpom-indonesia-certification",
      tag: "Chứng nhận",
      title: "QIANDU Indonesia đã đạt chứng nhận BPOM, trong đó MELOMELI đã hoàn tất BPOM và đăng ký thành công tại Indonesia.",
      summary:
        "Đây là bước tiến quan trọng về tuân thủ pháp lý của QIANDU tại Indonesia, đồng thời xác nhận MELOMELI đã hoàn tất điều kiện đăng ký cần thiết để vào thị trường.",
      date: "06/2026",
      image: "/company-news/melomeli-huonggiang-02.jpg",
      imageFit: "contain",
      detailEyebrow: "Chứng nhận",
      detailTitle: "QIANDU Indonesia đạt chứng nhận BPOM, và MELOMELI đã hoàn tất BPOM cùng đăng ký tại Indonesia.",
      intro:
        "QIANDU Indonesia đã hoàn tất tiến trình chứng nhận liên quan đến BPOM, trong đó MELOMELI đã chính thức vượt qua BPOM và hoàn tất đăng ký sản phẩm tại Indonesia. Đây là cột mốc quan trọng cho việc gia nhập thị trường một cách tuân thủ và bài bản hơn.",
      sections: [
        {
          title: "Tiến triển pháp lý",
          body:
            "Chứng nhận BPOM giúp củng cố năng lực vận hành tuân thủ của QIANDU tại Indonesia, đồng thời tạo nền tảng rõ ràng hơn cho việc phát triển thương hiệu mỹ phẩm Hàn Quốc tại thị trường này.",
        },
        {
          title: "Đăng ký thương hiệu",
          body:
            "MELOMELI đã hoàn tất chứng nhận BPOM và đăng ký tại Indonesia, cho thấy thương hiệu đã đáp ứng điều kiện pháp lý quan trọng để được triển khai chính thức tại thị trường địa phương.",
        },
        {
          title: "Ý nghĩa thị trường",
          body:
            "Với QIANDU, đây không chỉ là một cập nhật về tuân thủ, mà còn là bước tiến thực chất cho hoạt động phân phối, mở rộng bán lẻ và xây dựng thương hiệu tại Indonesia.",
        },
      ],
      highlights: ["Indonesia", "BPOM", "MELOMELI"],
    },
    {
      slug: "peak-jmsolution-bangkok-activation",
      tag: "Tin doanh nghiệp",
      title: "Ngày 30 tháng 5 năm 2026, QIANDU GROUP phối hợp cùng Wishco và nghệ sĩ Thái Lan PEAK triển khai hoạt động retail activation cho JMsolution tại Bangkok.",
      summary:
        "Hoạt động này tập trung vào tăng cường độ hiện diện bán lẻ và hỗ trợ mở rộng kênh offline cho JMsolution tại thị trường Bangkok.",
      date: "30/05/2026",
      image: "/company-news/peak-jmsolution-03.jpg",
      imageFit: "contain",
      detailEyebrow: "Tin doanh nghiệp",
      detailTitle: "QIANDU GROUP phối hợp cùng Wishco và PEAK triển khai retail activation cho JMsolution tại Bangkok.",
      intro:
        "Ngày 30 tháng 5 năm 2026, QIANDU GROUP phối hợp cùng Wishco và nghệ sĩ Thái Lan PEAK tổ chức hoạt động retail activation cho JMsolution tại Bangkok. Chương trình tập trung vào nhận diện thương hiệu, truyền thông tại điểm bán và mở rộng độ hiện diện trong kênh offline.",
      sections: [
        {
          title: "Tổng quan sự kiện",
          body:
            "Hoạt động kết hợp hình ảnh thương hiệu, sự xuất hiện của người nổi tiếng và truyền thông bán lẻ để tạo ấn tượng rõ ràng hơn cho JMsolution tại thị trường địa phương.",
        },
        {
          title: "Ý nghĩa thị trường",
          body:
            "Đây không chỉ là một hoạt động quảng bá ngắn hạn mà còn là bước triển khai thực tế nhằm hỗ trợ thương hiệu mỹ phẩm Hàn Quốc tiếp cận chuỗi mỹ phẩm offline tại Thái Lan.",
        },
        {
          title: "Giá trị thực thi",
          body:
            "Chương trình cho thấy năng lực của QIANDU trong việc kết nối hợp tác thương hiệu, triển khai khu vực và truyền thông bản địa thành một mô hình vận hành rõ ràng hơn cho thị trường Đông Nam Á.",
        },
      ],
      highlights: ["Bangkok", "JMsolution", "Retail activation"],
      galleryImages: [
        { src: "/company-news/peak-jmsolution-04.jpg", alt: "PEAK and QIANDU GROUP representative at the Bangkok activation", fit: "contain" },
        { src: "/company-news/peak-jmsolution-03.jpg", alt: "PEAK with JMsolution campaign visual", fit: "contain" },
        { src: "/company-news/peak-jmsolution-02.jpg", alt: "QIANDU and Wishco event stage", fit: "contain" },
        { src: "/company-news/peak-jmsolution-01.jpg", alt: "PEAK with QIANDU JMsolution signage", fit: "contain" },
      ],
    },
    {
      slug: "huong-giang-melomeli-partnership",
      tag: "Cập nhật hợp tác",
      title: "Huong Giang chính thức trở thành đối tác truyền thông cho chiến dịch quảng bá nước tẩy trang MELOMELI của Hàn Quốc tại thị trường Việt Nam.",
      summary:
        "Hợp tác này nhằm tăng cường độ nhận diện thương hiệu MELOMELI tại Việt Nam thông qua chiến dịch truyền thông bản địa hóa với người nổi tiếng.",
      date: "05/2026",
      image: "/company-news/melomeli-huonggiang-01.jpg",
      imageFit: "contain",
      detailEyebrow: "Cập nhật hợp tác",
      detailTitle: "Huong Giang tham gia chiến dịch truyền thông cho nước tẩy trang MELOMELI tại Việt Nam.",
      intro:
        "QIANDU xác nhận Huong Giang chính thức tham gia chiến dịch truyền thông cho nước tẩy trang MELOMELI của Hàn Quốc tại Việt Nam. Mục tiêu của hợp tác là mở rộng nhận diện thương hiệu và tăng hiệu quả truyền thông bản địa hóa cho sản phẩm chủ lực.",
      sections: [
        {
          title: "Phạm vi hợp tác",
          body:
            "Hợp tác tập trung vào nội dung truyền thông, độ phủ chiến dịch và câu chuyện thương hiệu xoay quanh dòng cleansing water của MELOMELI tại thị trường Việt Nam.",
        },
        {
          title: "Giá trị thương hiệu",
          body:
            "MELOMELI hiện được định hướng bằng ngôn ngữ thương hiệu rõ ràng hơn cho nhóm sản phẩm làm sạch và chăm sóc da cơ bản. Hợp tác này giúp chuyển hóa định vị đó thành độ nhận diện thực tế trên thị trường.",
        },
        {
          title: "Ý nghĩa khu vực",
          body:
            "Đối với QIANDU, đây là ví dụ điển hình cho mô hình kết nối thương hiệu Hàn Quốc với đối tác truyền thông bản địa và triển khai phù hợp theo từng thị trường Đông Nam Á.",
        },
      ],
      highlights: ["Việt Nam", "MELOMELI", "Celebrity-led campaign"],
      galleryImages: [
        { src: "/company-news/melomeli-huonggiang-01.jpg", alt: "Huong Giang for MELOMELI campaign", fit: "contain" },
        { src: "/company-news/melomeli-huonggiang-02.jpg", alt: "MELOMELI cleansing water exposure", fit: "contain" },
      ],
    },
    {
      slug: "qiandu-portfolio-update-2026",
      tag: "Doanh nghiệp",
      title: "Danh mục K-Beauty của QIANDU tiếp tục được hoàn thiện.",
      summary:
        "QIANDU tiếp tục tối ưu nhóm thương hiệu trọng điểm và sản phẩm chủ lực để phục vụ hợp tác phân phối và trình bày thương hiệu trên thị trường quốc tế.",
      date: "06/2026",
      image: "/official-brands/roundlab.jpg",
      detailEyebrow: "Cập nhật doanh nghiệp",
      detailTitle: "QIANDU tiếp tục hoàn thiện danh mục thương hiệu K-Beauty cho đối tác toàn cầu.",
      intro:
        "QIANDU đang tiếp tục tinh chỉnh cấu trúc danh mục thương hiệu dựa trên định vị rõ ràng hơn, nhóm sản phẩm chủ lực, và khả năng trình bày phù hợp hơn cho đối tác phân phối quốc tế.",
      sections: [
        {
          title: "Định hướng danh mục",
          body:
            "Trọng tâm hiện tại là nâng cao khả năng nhận diện cho các thương hiệu Hàn Quốc trọng điểm và làm rõ hơn câu chuyện của từng nhóm sản phẩm chủ lực.",
        },
        {
          title: "Mức độ sẵn sàng hợp tác",
          body:
            "Việc lựa chọn thương hiệu và cách trình bày đang được điều chỉnh để phù hợp hơn với nhu cầu phân phối, kênh bán lẻ và thảo luận gia nhập thị trường APAC.",
        },
      ],
      highlights: ["Danh mục thương hiệu", "Sản phẩm chủ lực", "Đối tác phân phối"],
    },
  ],
};

export function getNewsArticles(locale: Locale) {
  return newsByLocale[locale];
}

export function getNewsArticle(locale: Locale, slug: string) {
  return newsByLocale[locale].find((item) => item.slug === slug) ?? null;
}
